// ============================================================================
// OPTIONAL WORK ITEM TYPES
// Workflow templates can declare "optional" item types (e.g. follow_up) that
// are hidden unless the client explicitly enables them. Enablement is stored
// per-client on work_item_number_sequences.config.show_in_type_filter — the
// same flag the type-filter pills already use.
// ============================================================================

import { normalizeSolutionItemTypes } from './itemTypes';
import { normalizeWorkflowContext } from '../work-items/sequenceScope';

export interface WorkItemSequenceLike {
  item_type?: string | null;
  /** Workflow scope — 'default' (or missing on pre-migration rows) is the
   * client-wide row; any other value scopes the row's config to that
   * workflow (e.g. 'visitor-management'). */
  workflow_context?: string | null;
  config?: unknown;
}

/**
 * Parse a sequence `config` value that may arrive as a JSON string or object.
 */
export function parseWorkItemSequenceConfig(config: unknown): Record<string, unknown> {
  if (!config) return {};
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }
  return typeof config === 'object' ? config as Record<string, unknown> : {};
}

/**
 * Effective `show_in_type_filter` value for an item type in a workflow scope:
 * the scoped row wins when present, otherwise the 'default' row.
 */
function typeFilterFlagInScope(
  sequences: readonly WorkItemSequenceLike[] | null | undefined,
  itemType: string,
  context?: string | null,
): boolean | undefined {
  const scope = normalizeWorkflowContext(context);
  const rows = (sequences || []).filter(
    (s) => typeof s?.item_type === 'string' && s.item_type.trim() === itemType,
  );
  const scoped = rows.find((r) => (r.workflow_context || 'default') === scope);
  const row = scoped ?? rows.find((r) => (r.workflow_context || 'default') === 'default');
  if (!row) return undefined;
  const flag = parseWorkItemSequenceConfig(row.config).show_in_type_filter;
  return typeof flag === 'boolean' ? flag : undefined;
}

/**
 * Item types a client has explicitly enabled for the type filter
 * (`config.show_in_type_filter === true`). Optional types only become visible
 * when present in this set. `context` scopes the evaluation to a workflow
 * (defaults to 'default', preserving legacy behavior).
 */
export function getExplicitlyShownTypeFilterIds(
  sequences: readonly WorkItemSequenceLike[] | null | undefined,
  context?: string | null,
): Set<string> {
  const shown = new Set<string>();
  const itemTypes = new Set<string>();
  (sequences || []).forEach((sequence) => {
    const itemType = typeof sequence?.item_type === 'string' ? sequence.item_type.trim() : '';
    if (itemType) itemTypes.add(itemType);
  });
  itemTypes.forEach((itemType) => {
    if (typeFilterFlagInScope(sequences, itemType, context) === true) {
      shown.add(itemType);
    }
  });
  return shown;
}

/**
 * Item types a client has explicitly hidden from the type filter
 * (`config.show_in_type_filter === false`). `context` scopes the evaluation
 * to a workflow (defaults to 'default').
 */
export function getExplicitlyHiddenTypeFilterIds(
  sequences: readonly WorkItemSequenceLike[] | null | undefined,
  context?: string | null,
): Set<string> {
  const hidden = new Set<string>();
  const itemTypes = new Set<string>();
  (sequences || []).forEach((sequence) => {
    const itemType = typeof sequence?.item_type === 'string' ? sequence.item_type.trim() : '';
    if (itemType) itemTypes.add(itemType);
  });
  itemTypes.forEach((itemType) => {
    if (typeFilterFlagInScope(sequences, itemType, context) === false) {
      hidden.add(itemType);
    }
  });
  return hidden;
}

/**
 * Optional item types declared by a workflow template config. Two declaration
 * styles are supported:
 *   config.item_types: [{ key: 'follow_up', optional: true, ... }]
 *   config.optional_item_types: ['follow_up']
 */
export function getOptionalItemTypesFromTemplateConfig(config: unknown): string[] {
  if (!config || typeof config !== 'object') return [];
  const record = config as Record<string, unknown>;
  const optionalTypes = new Set<string>();

  normalizeSolutionItemTypes(record.optional_item_types).forEach((type) => optionalTypes.add(type));

  const itemTypes = record.item_types;
  if (Array.isArray(itemTypes)) {
    itemTypes.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const candidate = entry as Record<string, unknown>;
      if (candidate.optional !== true) return;
      normalizeSolutionItemTypes(candidate.key).forEach((type) => optionalTypes.add(type));
    });
  }

  return Array.from(optionalTypes);
}

/**
 * Optional types that are currently disabled for the client — i.e. declared
 * optional by the template but NOT explicitly enabled via the type-filter flag.
 * When `sequences` is undefined (still loading), optional types are treated as
 * disabled — the feature is opt-in, so it stays hidden until the config proves
 * it was explicitly enabled.
 */
export function getDisabledOptionalItemTypes(
  optionalItemTypes: Iterable<string>,
  sequences: readonly WorkItemSequenceLike[] | null | undefined,
  context?: string | null,
): Set<string> {
  const shown = getExplicitlyShownTypeFilterIds(sequences, context);
  const disabled = new Set<string>();
  for (const type of optionalItemTypes) {
    if (!shown.has(type)) disabled.add(type);
  }
  return disabled;
}
